import { useEffect, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { ErrorBoundary } from 'react-error-boundary'
import Zendesk, { ZendeskAPI } from 'react-zendesk'

import { cn } from '@/utils/utils'
import {
  useAuthDataState,
  useMasqueradeState,
} from '@/routes/shared/_authDataState'
import { useFeatureFlags } from '@/rules/get-feature-flags'
import { useUnreadDataQuery } from './api/get-unread-messages'

import ContactUs from './components/contact-us'
import { FallbackError } from './error'
import Footer from '../shared/Footer'
import Login from '../shared/Login'
import MasqueradeBanner from '../shared/MasqueradeBanner'
import { Header } from './components/header'
import { Nav } from './components/nav'
import { TooltipProvider } from '@/components/ui/tooltip'

interface DashboardProps {
  defaultLayout?: number[] | undefined
  defaultCollapsed?: boolean
}

const Dashboard = ({ defaultCollapsed = false }: DashboardProps) => {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed)
  const { isLoading: userIsLoading, data: authData } = useAuthDataState()
  const {
    actionLabel,
    actionUrl,
    isMasquerading,
    memberName,
    shouldClearAuthCookies,
  } = useMasqueradeState(authData)
  const { data: featureFlagsData } = useFeatureFlags()

  const location = useLocation()

  const { data: unreadData } = useUnreadDataQuery()

  useEffect(() => {
    scrollTo(0, 0)
  }, [location])

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed)
  }

  if (userIsLoading) return null
  if (!authData) return <Login />

  return (
    <ErrorBoundary FallbackComponent={FallbackError}>
      {featureFlagsData && (
        <TooltipProvider delayDuration={0}>
          <div className="h-full w-full flex-col items-center overflow-hidden">
            <MasqueradeBanner
              masquerading={isMasquerading}
              memberName={memberName}
              actionLabel={actionLabel}
              actionUrl={actionUrl}
              shouldClearAuthCookies={shouldClearAuthCookies}
            />
            <Header />
            <div className="w-full h-full p-6 mx-auto bg-slate-100">
              <div className="max-w-[1400px] mx-auto text-[#202020]">
                We’re excited to share that RealtyMogul has recently been
                acquired by a collective of real estate operators led by the
                Wideman Company. We invite you to review the{' '}
                <a
                  href="https://realtymogul.brightspotgocdn.com/cf/54/2b53aec04bdb9a64b7a161bd9c6e/letter-to-investors-from-twc.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary underline"
                >
                  Letter to Investors
                </a>{' '}
                from The Wideman Company for several platform-wide improvements
                being implemented.
              </div>
            </div>
            <div className="w-full box-border md:pt-6 pb-10 md:px-8 overflow-hidden xl:max-w-[1800px] flex flex-col mx-auto md:flex-row md:gap-6">
              <div
                className={cn(
                  'md:h-[70vh] md:pr-2 transition-all duration-300 ease-in-out',
                  isCollapsed && 'min-w-[75px] items-start justify-start'
                )}
              >
                <Nav
                  unreadCount={unreadData}
                  isCollapsed={isCollapsed}
                  toggleCollapse={toggleCollapse}
                  links={[
                    {
                      title: 'Investments',
                      icon: 'chart-line',
                      location: '/dashboard/investments/active',
                      active: location.pathname.includes('investments'),
                    },
                    {
                      title: 'Messages',
                      icon: 'envelope',
                      location: '/dashboard/messages',
                      active: location.pathname === '/dashboard/messages',
                    },
                    {
                      title: 'Tax Center',
                      icon: 'files',
                      location: '/dashboard/taxes',
                      active: location.pathname.includes('taxes'),
                    },
                    {
                      title: 'Transactions',
                      icon: 'rectangle-list',
                      location: '/dashboard/transactions',
                      active: location.pathname.includes('transactions'),
                    },
                  ]}
                  links2={[
                    {
                      title: 'Bank Accounts',
                      icon: 'bank',
                      location: '/dashboard/bank-accounts',
                      active: location.pathname.includes('bank-accounts'),
                    },
                    {
                      title: 'Ownership Types',
                      icon: 'user-group',
                      location: '/dashboard/ownership-types',
                      active: location.pathname.includes('ownership-types'),
                    },
                    {
                      title: 'Profile',
                      icon: 'user',
                      location: '/dashboard/profile',
                      active: location.pathname.includes('profile'),
                    },
                  ]}
                />
              </div>
              <div className="flex flex-col w-full md:px-[20px]">
                <div className="flex-grow md:mb-10">
                  <Outlet />
                </div>
                <ContactUs
                  links={[
                    {
                      label: 'Questions? Send a message',
                      to: () => ZendeskAPI('webWidget', 'open'),
                    },
                  ]}
                />
              </div>
            </div>
            <div className="w-full bg-black">
              <Footer />
            </div>
          </div>
          <Zendesk defer zendeskKey="f0fb7231-d555-4b6c-b382-06ff3c2efb4e" />
        </TooltipProvider>
      )}
    </ErrorBoundary>
  )
}

export default Dashboard
